import { PerformanceEmployeePerformanceInsightsSummaryFilter } from 'generated/resources/datatable.graphql'
import {
  AnalyticsPageEmployeeNamesFragment,
  AnalyticsPageLegalEntitiesFragment,
  AnalyticsPageLocationsFragment,
} from 'generated/resources/graphql'

import type { Filter } from 'modules/core/components/DataTable/types'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import type { ChartClickDatum } from 'modules/performance_analytics/components/Headcount/lib/chartInteractionTypes'
import {
  createUpdatedFilter,
  findTargetFilter,
  getCurrentFilterValues,
  isFilterEnabled,
} from 'modules/performance_analytics/components/Headcount/lib/filterOperations'
import { createFilterValueTransformer } from 'modules/performance_analytics/components/Headcount/lib/filterValueTransformers'
import type { GroupByType } from 'modules/performance_analytics/components/helpers'
import { TeamsTeam } from 'modules/performance_analytics/hooks/useGroupByKeysLookupTransformer'

// Helper to prevent dropdown auto-opening when filters are applied programmatically
const preventDropdownAutoOpen = <T extends { noAutoOpen?: boolean }>(filter: T): void => {
  filter.noAutoOpen = true
}

interface UseHeadcountChartInteractionProps {
  groupBy?: GroupByType
  dataTableFilters: Record<string, unknown>
  enabledFilters: Filter<PerformanceEmployeePerformanceInsightsSummaryFilter>[]
  availableFilters: Filter<PerformanceEmployeePerformanceInsightsSummaryFilter>[]
  onAddFilter: (filter: Filter<PerformanceEmployeePerformanceInsightsSummaryFilter>) => void
  onUpdateFilter: (filter: Filter<PerformanceEmployeePerformanceInsightsSummaryFilter>) => void
  legalEntities?: AnalyticsPageLegalEntitiesFragment[]
  teams?: Pick<TeamsTeam, 'id' | 'name'>[]
  locations?: AnalyticsPageLocationsFragment[]
  employeesNames?: AnalyticsPageEmployeeNamesFragment[]
}

export const useHeadcountChartInteraction = ({
  groupBy,
  dataTableFilters,
  enabledFilters,
  availableFilters,
  onAddFilter,
  onUpdateFilter,
  legalEntities,
  teams,
  locations,
  employeesNames,
}: UseHeadcountChartInteractionProps) => {
  // Handles chart bar clicks to apply/toggle filters programmatically
  const handleChartClick = (datum: ChartClickDatum) => {
    if (!groupBy) return

    // Find the target filter
    const targetFilter = findTargetFilter(groupBy, availableFilters, enabledFilters)
    if (!targetFilter) return

    // Track the interaction
    track(trackingEvents.performanceAnalytics.insightsSummaryHeadcountBarClicked, {
      category: datum.label,
      groupBy,
    })

    // Get current filter values
    const currentValues = getCurrentFilterValues(groupBy, dataTableFilters)

    // Transform the clicked value and toggle it
    const transformer = createFilterValueTransformer({
      groupBy,
      legalEntities,
      teams,
      locations,
      employeesNames,
    })

    const newValues = transformer.handleClickTransformation(datum.label, currentValues)
    const updatedFilter = createUpdatedFilter(targetFilter, newValues)

    preventDropdownAutoOpen(updatedFilter)

    // Apply the filter
    if (isFilterEnabled(groupBy, enabledFilters)) {
      onUpdateFilter(updatedFilter)
    } else {
      onAddFilter(updatedFilter)
    }
  }

  return {
    handleChartClick,
  }
}
